import { reCAPTCHAv3 } from "commonAssets/js/constants";

/**
 * Mints a fresh reCAPTCHA Enterprise token immediately before the claim POST.
 *
 * The base flow (common/assets/js/submit-claim.js) mints the token in
 * process_claim and only then calls post_claim, which uploads every attachment
 * to S3 sequentially before posting the claim. Enterprise tokens are valid for
 * 120 seconds, so a claimant with attachments on a slow connection can spend
 * the entire window uploading and have the claim POST rejected with
 * tokenProperties.invalidReason = "EXPIRED".
 *
 * The failure is invisible from the claimant's side: the form validates clean,
 * every file lands in S3 with failed:false, and only the claim itself is lost.
 * Retrying re-uploads the same files and burns the window again, so the
 * claimant loops.
 *
 * Re-minting here starts the 120s clock at the only request that actually
 * verifies the token. The S3 upload path (s3Uploader + the signed PUT) is gated
 * by the static x-api-key and never reads this token, so moving the mint later
 * leaves nothing unprotected.
 *
 * @param {string} fallback - Existing token, returned if minting is impossible.
 * @returns {Promise<string>} A fresh token, or the fallback on failure.
 */
export async function refreshRecaptchaToken(fallback) {
    if (
        typeof grecaptcha === "undefined" ||
        typeof grecaptcha.enterprise === "undefined"
    ) {
        return fallback;
    }

    try {
        return await grecaptcha.enterprise.execute(reCAPTCHAv3, {
            action: "submit",
        });
    } catch (error) {
        // A refresh failure must not lose the claim -- post with the original
        // token and let the backend decide.
        console.warn(
            "[refreshRecaptchaToken] refresh failed, posting with the original token",
            error
        );
        return fallback;
    }
}
